import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { translations, LANGUAGES } from "../i18n/translations";

// Master switch for all translation features (per-deal translate + header language switcher).
// Set to true to re-enable. While false, the whole UI stays in English.
export const TRANSLATION_ENABLED = true;

const LanguageContext = createContext();

const SUPPORTED = LANGUAGES.map((l) => l.code);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (!TRANSLATION_ENABLED) return "en";
    const saved = localStorage.getItem("lp-lang");
    if (saved && SUPPORTED.includes(saved)) return saved;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return SUPPORTED.includes(nav) ? nav : "en";
  });

  const setLang = useCallback((l) => {
    if (!TRANSLATION_ENABLED || !SUPPORTED.includes(l)) return;
    setLangState(l);
    localStorage.setItem("lp-lang", l);
    document.documentElement.setAttribute("lang", l);
    setAutoLang(null);
    localStorage.removeItem("lp-lang-auto");
  }, []);

  // When we auto-pick a non-English UI from the visitor's region, remember it so we can offer a
  // one-time "View in English?" escape hatch in case the guess is wrong.
  const [autoLang, setAutoLang] = useState(() => localStorage.getItem("lp-lang-auto") || null);
  const clearAutoLang = useCallback(() => {
    setAutoLang(null);
    localStorage.removeItem("lp-lang-auto");
  }, []);

  // First visit only: if the visitor hasn't chosen a language and their device didn't already give
  // us a supported one, fall back to the language of the country we detect them in (by IP).
  useEffect(() => {
    if (!TRANSLATION_ENABLED) return;
    if (localStorage.getItem("lp-lang") || lang !== "en") return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/ip-country`)
      .then((r) => r.json())
      .then((d) => {
        if (d && d.lang && SUPPORTED.includes(d.lang) && d.lang !== "en") {
          setLang(d.lang);
          setAutoLang(d.lang);
          localStorage.setItem("lp-lang-auto", d.lang);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback((key, vars) => {
    const dict = translations[lang] || translations.en;
    let str = dict[key] ?? translations.en[key] ?? key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
      }
    }
    return str;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages: LANGUAGES, autoLang, clearAutoLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
